import { useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { ChevronLeft, ShieldCheck } from 'lucide-react';
import { pairKey, readCachedRunResults } from '@/lib/interactions';
import { SeverityColors } from '@/lib/theme';
import SeverityBadge from '@/components/SeverityBadge';

export default function InteractionDetailPage() {
  const navigate = useNavigate();
  const { pairId } = useParams();
  const [searchParams] = useSearchParams();
  const drugA = searchParams.get('drugA') || '';
  const drugB = searchParams.get('drugB') || '';
  const wantedId = pairId || pairKey(drugA, drugB);

  const [result, setResult] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    const cached = readCachedRunResults();
    const hit = cached.find((r) => pairKey(r.drug_a, r.drug_b) === wantedId) ?? null;
    if (hit) {
      setResult(hit);
    } else {
      setNotFound(true);
    }
  }, [wantedId]);

  if (!result) {
    return (
      <div className="max-w-2xl mx-auto px-4 sm:px-6 py-6">
        <div className="flex items-center gap-2 mb-6">
          <button onClick={() => navigate(-1)} data-testid="interaction-detail-back" className="p-1 rounded-lg hover:bg-black/5">
            <ChevronLeft className="h-6 w-6" style={{ color: '#5B2D1F' }} />
          </button>
          <h1 className="text-2xl font-extrabold" style={{ color: '#5B2D1F' }}>Interaction</h1>
        </div>
        <div className="flex flex-col items-center justify-center py-16" data-testid="interaction-detail-loading">
          {notFound ? (
            <div className="text-center px-6">
              <h2 className="text-xl font-extrabold mb-2" style={{ color: '#0F2A35' }}>Result not found</h2>
              <p className="text-base mb-6" style={{ color: '#4B6B79' }}>Re-run the analysis from the interactions screen to see this pair's details.</p>
              <button onClick={() => navigate('/interactions', { replace: true })} data-testid="interaction-detail-rerun" className="px-8 py-4 rounded-xl font-extrabold text-white" style={{ backgroundColor: '#D77A52' }}>
                Run analysis
              </button>
            </div>
          ) : (
            <p className="text-base" style={{ color: '#4B6B79' }}>Loading…</p>
          )}
        </div>
      </div>
    );
  }

  const c = SeverityColors[result.severity] || SeverityColors.uncertain;

  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-6 py-6 pb-12" data-testid="interaction-detail-screen">
      <div className="flex items-center gap-2 mb-4">
        <button onClick={() => navigate(-1)} data-testid="interaction-detail-back" className="p-1 rounded-lg hover:bg-black/5">
          <ChevronLeft className="h-6 w-6" style={{ color: '#5B2D1F' }} />
        </button>
        <h1 className="text-2xl font-extrabold" style={{ color: '#5B2D1F' }} data-testid="interaction-detail-title">Interaction</h1>
      </div>

      {/* Hero */}
      <div data-testid="interaction-detail-hero" className="rounded-2xl border-[1.5px] p-6 mb-6 space-y-2" style={{ backgroundColor: c.bg, borderColor: c.border }}>
        <SeverityBadge severity={result.severity} size="md" />
        <h2 className="text-2xl font-extrabold mt-1" style={{ color: '#0F2A35' }} data-testid="interaction-detail-pair">{result.drug_a} × {result.drug_b}</h2>
        <p className="text-base font-semibold leading-relaxed" style={{ color: c.text }} data-testid="interaction-detail-summary">{result.summary}</p>
      </div>

      {/* Explanation */}
      <div className="rounded-2xl border p-6 mb-4" style={{ backgroundColor: '#FFFFFF', borderColor: '#D7E2E8' }}>
        <p className="text-xs font-extrabold uppercase tracking-widest mb-2" style={{ color: '#4B6B79' }}>What this means</p>
        <p className="text-base leading-relaxed" style={{ color: '#0F2A35' }} data-testid="interaction-detail-explanation">{result.explanation}</p>
      </div>

      {/* Recommendation */}
      <div className="rounded-2xl p-6 mb-4" style={{ backgroundColor: '#EEF4F7' }}>
        <div className="flex items-center gap-1.5 mb-2">
          <ShieldCheck className="h-4 w-4" style={{ color: '#5B2D1F' }} />
          <p className="text-xs font-extrabold uppercase tracking-widest" style={{ color: '#5B2D1F' }}>Recommendation</p>
        </div>
        <p className="text-base leading-relaxed" style={{ color: '#0F2A35' }} data-testid="interaction-detail-recommendation">{result.recommendation}</p>
      </div>

      {/* Sources */}
      <div className="rounded-2xl border p-6 mb-4" style={{ backgroundColor: '#FFFFFF', borderColor: '#D7E2E8' }}>
        <p className="text-xs font-extrabold uppercase tracking-widest mb-3" style={{ color: '#4B6B79' }}>Sources</p>
        <div className="flex flex-wrap gap-2" data-testid="interaction-detail-sources">
          {(result.sources || []).map((s, i) => (
            <span key={`${s}-${i}`} className="px-4 py-1.5 rounded-full text-sm font-bold" style={{ backgroundColor: '#EEF4F7', color: '#5B2D1F' }}>{s}</span>
          ))}
        </div>
      </div>

      {/* Disclaimer */}
      <div className="rounded-xl border p-4" style={{ backgroundColor: '#FFF4DF', borderColor: '#E0B96F' }}>
        <p className="text-sm leading-relaxed" style={{ color: '#5C3B0E' }}>
          Not a substitute for professional medical advice. Always confirm interactions with your pharmacist or physician before changing how you take your medications.
        </p>
      </div>
    </div>
  );
}
